import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import type { StateCreator } from 'zustand';
import { WebSocketStatusEnum } from '../../enums/WebSocketStatusEnum';
import type { WebSocketMessage } from '../../interfaces/WebSocketMessage';
import { useAppStore } from '../store';

/**
 * Zustand slice for managing the EventHub SignalR WebSocket connection (notifications/AI).
 */
export type WebSocketSlice = {
  webSocket: {
    /** Current status of the EventHub WebSocket connection. */
    status: WebSocketStatusEnum;
    /** Error from last connection attempt, if any. */
    lastError?: string;
    /** Circular buffer of received WebSocketMessages (up to _maxBuffer). */
    messages: WebSocketMessage[];

    /** Connect to the EventHub SignalR hub. */
    connect: (urlOverride?: string, token?: string) => Promise<void>;
    /** Disconnect from the EventHub SignalR hub. */
    disconnect: () => Promise<void>;
    /** Send a message to the EventHub SignalR hub. */
    send: (data: unknown) => Promise<boolean>;

    // Internal state:
    /** Reference to the current EventHub HubConnection instance. */
    _connection?: HubConnection | null;
    /** URL of the active EventHub SignalR hub. */
    _url?: string;
    /** Consecutive retry count for (re)connection attempts. */
    _retryCount: number;
    /** Maximum number of messages to retain. */
    _maxBuffer: number;
    /** Flag to indicate if we should auto-reconnect. */
    _shouldReconnect: boolean;
  };
};

// ❗️If you don't use devtools, leave it like this:
// type WithMW = [];
// Se usi devtools/subscribeWithSelector, usa la tupla identica a quella dello store:
type WithMW = []; // change to [['zustand/devtools', never], ['zustand/subscribeWithSelector', never]] if necessary

/**
 * Zustand state creator for WebSocket/SignalR integration.
 *
 * Handles connection lifecycle, message handling, reconnection,
 * proper state cleanup, and provides easy hooks for React UI.
 */
export const createWebSocketSlice: StateCreator<WebSocketSlice, WithMW, [], WebSocketSlice> = (
  set,
  get,
) => ({
  webSocket: {
    status: WebSocketStatusEnum.Idle,
    messages: [],
    _connection: null,
    _url: undefined,
    _retryCount: 0,
    _maxBuffer: 500,
    _shouldReconnect: true,

    /**
     * Establish a new SignalR connection.
     *
     * @param urlOverride - Optionally specify a different hub URL.
     * @param token - JWT Bearer token for auth.
     */
    connect: async (urlOverride, token) => {
      // Empty srToken (file-scoped OTP session) = no real-time connection.
      // Only skip when callers pass an explicit empty token; omit/undefined keeps prior behavior.
      if (typeof token === 'string' && token.trim() === '') {
        return;
      }

      const { webSocket } = get();
      // Prefer explicit, then stored, then env URL
      const envUrl = import.meta.env.VITE_APP_WEBSOCKET_URL || undefined;
      const baseUrl = urlOverride ?? webSocket._url ?? envUrl;
      const targetUrl = baseUrl ? `${baseUrl}/hubs/events` : undefined;

      if (!targetUrl) {
        // Fail fast if no URL available
        set((store) => ({
          webSocket: { ...store.webSocket, lastError: 'Missing SignalR Hub URL' },
        }));
        return;
      }

      // Prevent duplicate connection attempts
      if (
        webSocket._connection &&
        (webSocket.status === WebSocketStatusEnum.Connecting ||
          webSocket.status === WebSocketStatusEnum.Open)
      ) {
        return;
      }

      // If an existing connection is alive, attempt a graceful stop before replacing.
      if (webSocket._connection) {
        try {
          await webSocket._connection.stop();
        } catch {
          // Ignore errors when stopping an existing connection.
        }
      }

      set((store) => ({
        webSocket: {
          ...store.webSocket,
          status: WebSocketStatusEnum.Connecting,
          _url: targetUrl,
          _shouldReconnect: true,
        },
      }));

      try {
        // Build HubConnection with signalr config
        const connectionBuilder = new HubConnectionBuilder()
          .withUrl(targetUrl, {
            accessTokenFactory: () => token || '',
            // Set a longer initial connection timeout for slow networks.
            timeout: 60000, // ms
          })
          // Enable automatic reconnects with backoff and a cap of 10 tries.
          .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: (retryContext) => {
              if (retryContext.previousRetryCount >= 10) {
                // After 10 failed attempts, don't retry further.
                console.warn('[SignalR] Max retry attempts reached');
                return null;
              }
              // Exponential backoff: 2^n seconds, up to 30s max.
              const delay = Math.min(1000 * 2 ** retryContext.previousRetryCount, 30000);
              return delay;
            },
          })
          // Keep connection alive and match backend config.
          .withServerTimeout(120000) // ms (2 minutes)
          .withKeepAliveInterval(60000); // ms (1 minute)

        const connection = connectionBuilder.build();

        set((store) => ({ webSocket: { ...store.webSocket, _connection: connection } }));

        // === EVENT HANDLERS ===

        // Triggered before reconnecting (e.g., dropped connection)
        connection.onreconnecting(() => {
          set((store) => ({
            webSocket: { ...store.webSocket, status: WebSocketStatusEnum.Reconnecting },
          }));
        });

        // Triggered after a successful automatic reconnect
        connection.onreconnected(() => {
          set((store) => ({
            webSocket: {
              ...store.webSocket,
              status: WebSocketStatusEnum.Open,
              _retryCount: 0,
              lastError: undefined,
            },
          }));
        });

        // Triggered once the connection is fully closed (possibly by server or failure)
        connection.onclose((error) => {
          set((store) => ({
            webSocket: {
              ...store.webSocket,
              status: WebSocketStatusEnum.Closed,
              _connection: null,
              lastError: error?.message,
            },
          }));
        });

        // Handler for incoming real-time events/messages of type 'ReceiveEvent'
        connection.on('ReceiveEvent', (message) => {
          let raw: unknown = message;
          try {
            raw = typeof message === 'string' ? JSON.parse(message) : message;
          } catch {
            // If not JSON, leave raw
          }

          const event = raw as WebSocketMessage;

          // Insert new message, respecting circular buffer size
          set((store) => {
            const next = [...store.webSocket.messages, event];
            if (next.length > store.webSocket._maxBuffer) {
              next.splice(0, next.length - store.webSocket._maxBuffer);
            }
            return { webSocket: { ...store.webSocket, messages: next } };
          });

          useAppStore.getState().events.emit.webSocketEvent(event);
        });

        // Manual connection startup timeout for resilience
        const connectionPromise = connection.start();
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Connection timeout after 15 seconds')), 15000);
        });

        // Wait for connection start, but limit time to 15s
        await Promise.race([connectionPromise, timeoutPromise]);

        set((store) => ({
          webSocket: {
            ...store.webSocket,
            status: WebSocketStatusEnum.Open,
            _retryCount: 0,
            lastError: undefined,
          },
        }));
      } catch (err: any) {
        // Catch, log, and update error state on failure to connect
        set((store) => ({
          webSocket: {
            ...store.webSocket,
            status: WebSocketStatusEnum.Error,
            lastError: err?.message ?? 'SignalR connection error',
            _connection: null,
          },
        }));
      }
    },

    /**
     * Gracefully disconnect from SignalR and clean up all handlers.
     */
    disconnect: async () => {
      const { webSocket } = get();
      const connection = webSocket._connection;

      // Prevent future reconnect attempts
      set((store) => ({ webSocket: { ...store.webSocket, _shouldReconnect: false } }));

      if (connection) {
        set((store) => ({
          webSocket: { ...store.webSocket, status: WebSocketStatusEnum.Closing },
        }));

        try {
          await connection.stop();
        } catch (err) {
          console.error('[SignalR] Error during disconnect:', err);
        }
      }

      set((store) => ({
        webSocket: { ...store.webSocket, _connection: null, status: WebSocketStatusEnum.Closed },
      }));
    },

    /**
     * Send data to the SignalR hub ("SendMessage" method).
     *
     * @param data - The outgoing data packet.
     * @returns Whether the send was successful.
     */
    send: async (data: unknown) => {
      const connection = get().webSocket._connection;
      // Only send if connection is open and ready
      if (!connection || connection.state !== 'Connected') return false;

      try {
        await connection.invoke('SendMessage', data);
        return true;
      } catch (err) {
        console.error('[SignalR] Failed to send message:', err);
        return false;
      }
    },
  },
});
